import { Button, Chip, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useListContext, useTranslate } from 'ra-core';
import { Close } from '@mui/icons-material';
import { ReactElement, useCallback } from 'react';
import { isEmpty } from 'lodash';
import { SaveFiltersButton } from './SaveFiltersButton';
import { useGetChipValue } from './FilterSidebarContext';

interface ChipItemProps {
  onDelete: () => void;
  value: unknown;
  source: string;
}

function ChipItem(props: ChipItemProps) {
  const { onDelete, value, source } = props;
  const label = useGetChipValue({ source, value });

  return (
    label && (
      <Chip
        label={label}
        // @ts-ignore
        variant="combined"
        color="primary"
        sx={{
          height: '36px',
          '& .MuiChip-deleteIcon': {
            fontSize: '1rem !important',
            mr: 1
          }
        }}
        deleteIcon={<Close />}
        onDelete={onDelete}
      />
    )
  );
}

function Chips(): ReactElement {
  const { filterValues, setFilters } = useListContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const translate = useTranslate();

  const resetAllFilters = useCallback(() => {
    // @ts-ignore
    setFilters({});
  }, [setFilters]);

  const removeFilter = useCallback(
    (key: string) => {
      const newFilters = { ...filterValues };
      delete newFilters[key];
      // @ts-ignore
      setFilters(newFilters);
    },
    [filterValues, setFilters]
  );

  const Buttons = useCallback(() => {
    return (
      <Stack flexDirection={'row'} gap={2}>
        <SaveFiltersButton />
        <Button variant="outlined" onClick={resetAllFilters}>
          {translate('ra.action.reset_filters')}
        </Button>
      </Stack>
    );
  }, [resetAllFilters, translate]);

  return (
    <Stack gap={2} flexDirection={isMobile ? 'column' : 'row'}>
      <Stack
        flexDirection={'row'}
        flexWrap={isMobile ? 'nowrap' : 'wrap'}
        gap={2}
        py={0.5}
        overflow={'auto'}
        width={isMobile ? window.innerWidth - (24 + 34) : 'auto'}
      >
        {Object.entries(filterValues).map(([key, value]) => {
          return <ChipItem key={key} source={key} value={value} onDelete={() => removeFilter(key)} />;
        })}
        {!isMobile ? <Buttons /> : null}
      </Stack>
      {isMobile ? <Buttons /> : null}
    </Stack>
  );
}

function ActiveFiltersChips(): ReactElement | null {
  const { filterValues } = useListContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const translate = useTranslate();

  return filterValues && !isEmpty(filterValues) ? (
    <Stack
      sx={{ backgroundColor: theme.palette.background.paper }}
      flexDirection={isMobile ? 'column' : 'row'}
      alignItems={'baseline'}
      gap={2}
      p={isMobile ? 2 : 0}
    >
      <Typography variant="h6" color="secondary" whiteSpace={'nowrap'}>
        {translate('ra.title.active_filters')}
      </Typography>
      <Chips />
    </Stack>
  ) : null;
}

export { ActiveFiltersChips };
